
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { ActivePopup } from './types';

export const useDatabasePopups = () => {
  const [activePopups, setActivePopups] = useState<ActivePopup[]>([]);
  const { user } = useAuth();

  // Check for database popups that should be shown to this user
  const checkDatabasePopups = useCallback(async () => {
    if (!user) {
      console.log('No user available for popup check');
      return;
    }

    try {
      console.log('Checking for broadcast popups for user:', user.id);
      
      // Get all active broadcast popups from the database
      const { data: broadcastPopups, error } = await supabase
        .from('broadcast_popups')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching broadcast popups:', error);
        return;
      }

      console.log('Found broadcast popups:', broadcastPopups?.length || 0);

      if (broadcastPopups && broadcastPopups.length > 0) {
        const validPopups = broadcastPopups.filter(dbPopup => {
          if (!dbPopup.popup_id || !dbPopup.title || !dbPopup.message) {
            console.warn('Popup missing required fields:', dbPopup);
            return false;
          }

          // Check if this user has dismissed this popup
          const dismissedByUsers = Array.isArray(dbPopup.dismissed_by_users) ? dbPopup.dismissed_by_users : [];
          const isDismissedByUser = dismissedByUsers.includes(user.id);
          
          // Check if this popup is already active for this user
          const alreadyActive = activePopups.find(p => p.dbId === dbPopup.id);
          
          console.log(`Popup ${dbPopup.popup_id} - dismissed: ${isDismissedByUser}, already active: ${!!alreadyActive}`);
          
          return !isDismissedByUser && !alreadyActive;
        });

        console.log('Valid popups to show:', validPopups.length);

        // Add all valid popups to the queue
        if (validPopups.length > 0) {
          const newPopups = validPopups.map(dbPopup => ({
            id: dbPopup.popup_id,
            title: dbPopup.title,
            message: dbPopup.message,
            style: (dbPopup.style || 'windows95') as any,
            priority: (dbPopup.priority || 'medium') as any,
            dismissible: Boolean(dbPopup.dismissible),
            persistForAllUsers: true,
            dbId: dbPopup.id
          }));

          setActivePopups(prev => {
            // Filter out any that already exist
            const existingIds = prev.map(p => p.dbId).filter(Boolean);
            const filteredNew = newPopups.filter(p => !existingIds.includes(p.dbId));
            
            if (filteredNew.length > 0) {
              console.log('Adding popups to queue:', filteredNew.map(p => p.id));
              return [...prev, ...filteredNew];
            }
            
            return prev;
          });
        }
      }
    } catch (error) {
      console.error('Error in checkDatabasePopups:', error);
    }
  }, [user, activePopups]);

  const dismissPopupInDatabase = useCallback(async (dbId: string, userId: string) => {
    if (!dbId || !userId) {
      console.error('Missing dbId or userId for dismiss operation');
      return;
    }

    try {
      console.log('Dismissing popup in database:', dbId, 'for user:', userId);
      
      const { error } = await supabase.rpc('dismiss_broadcast_popup', {
        popup_id: dbId,
        user_id: userId
      });
      
      if (error) {
        console.error('Error dismissing popup:', error);
        throw error;
      } else {
        console.log('Popup dismissed successfully in database');
      }
    } catch (error) {
      console.error('Error calling dismiss function:', error);
      throw error;
    }
  }, []);

  const storeBroadcastPopup = useCallback(async (content: any) => {
    if (!content || !content.id || !content.title || !content.message) {
      console.error('Invalid popup content provided:', content);
      throw new Error('Invalid popup content - missing required fields');
    }

    try {
      console.log('Creating broadcast popup using direct insert:', content);
      
      // Insert directly into broadcast_popups table
      const { data, error } = await supabase
        .from('broadcast_popups')
        .insert({
          popup_id: content.id,
          title: content.title,
          message: content.message,
          style: content.style || 'windows95',
          priority: content.priority || 'medium',
          dismissible: Boolean(content.dismissible),
          is_active: true,
          dismissed_by_users: []
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating broadcast popup:', error);
        throw error;
      } 
      
      console.log('Broadcast popup created successfully:', data);
      return { id: data.id };
    } catch (error) {
      console.error('Error in storeBroadcastPopup:', error);
      throw error;
    }
  }, []);

  return {
    activePopups,
    setActivePopups,
    checkDatabasePopups,
    dismissPopupInDatabase,
    storeBroadcastPopup
  };
};
